import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Globe, Search, ExternalLink, Copy, RotateCcw, SlidersHorizontal, Filter, Tag, Eye, Plus } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  DataTable,
  StatusPill,
  EmptyState,
  Pagination,
  FilterBuilder,
  type ColumnDef,
  type FilterField,
  type FilterCondition,
} from '@vnetwork/ui/data';
import {
  Input,
  Badge,
  Skeleton,
  IconButton,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Tooltip,
  TooltipProvider,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDomains,
  useWafPlatforms,
  useWafTags,
  useUpdateWafDomainSettings,
  fmtEpoch,
  wafExtractError,
  type WafDomain,
  type WafTag,
} from '@/api/waf';
import { useDebounce } from '../../shared';
import { TagEditor } from './TagEditor';
import { cap, sslTypeLabel } from './format';
import './WafDomainList.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// Filterable fields → backend query params (all comma-separated / "in" match).
// Order here is the order shown in the FilterBuilder field picker.
const FILTER_FIELD_IDS = ['status', 'platform_status', 'platform_name', 'tag', 'origin_source', 'cdn_cname'] as const;
type FilterFieldId = (typeof FILTER_FIELD_IDS)[number];

// Domain (zone) status values come straight from Mlytics (no enum mapping in the
// BE). Note the vendor spelling "is_progress" here vs "in_progress" for platforms.
const STATUS_OPTIONS = [
  { value: 'updating', label: 'Updating' },
  { value: 'is_progress', label: 'Deploying' },
  { value: 'failed', label: 'Unsuccessful' },
  { value: 'delete_in_progress', label: 'Deleting' },
  { value: 'usable', label: 'Usable' },
  { value: 'completed', label: 'Operational' },
];
// Platform (CDN deployment) status — different vendor spelling ("in_progress").
const PLATFORM_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'Installing' },
  { value: 'completed', label: 'Installed' },
  { value: 'delete_in_progress', label: 'Deleting' },
  { value: 'failed', label: 'Failed' },
];

// Columns the user can toggle via "Display options" (Domain is fixed).
const TOGGLEABLE = [
  { id: 'cname', label: 'CNAME' },
  { id: 'status', label: 'Status' },
  { id: 'waf', label: 'WAF' },
  { id: 'ssl', label: 'SSL' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'tags', label: 'Tags' },
  { id: 'created', label: 'Created' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = {
  cname: true, status: true, waf: true, ssl: true, platforms: true, tags: true, created: true,
};

type Tone = 'success' | 'danger' | 'warning' | 'muted';

/** Map a Mlytics zone status to a StatusPill tone. */
function statusTone(status?: string): Tone {
  const s = (status ?? '').toLowerCase();
  if (s === 'active' || s === 'enabled' || s === 'ready') return 'success';
  if (s === 'pending' || s === 'processing' || s === 'verifying') return 'warning';
  if (s === 'suspended' || s === 'disabled' || s === 'deleted' || s === 'error') return 'danger';
  return 'muted';
}

const muted = (v: React.ReactNode) => <span style={{ color: 'var(--vn-fg-muted)' }}>{v}</span>;

const boolBadge = (on: boolean, onLabel: string, offLabel = 'Off') =>
  on ? (
    <Badge tone="success" appearance="subtle" size="sm">{onLabel}</Badge>
  ) : (
    <Badge tone="neutral" appearance="subtle" size="sm">{offLabel}</Badge>
  );

/** Tags column cell — read-only chips (editing lives in the Actions column). */
function TagsCell({ tags }: { tags: string[] }) {
  if (!tags.length) return muted('—');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {tags.map((t) => (
        <Badge key={t} tone="info" appearance="subtle" size="sm">
          {t}
        </Badge>
      ))}
    </span>
  );
}

/** Row actions: copy CNAME, edit tags (popover), view detail — all tooltipped. */
function ActionsCell({
  serviceId,
  domain,
  cname,
  tags,
  options,
  href,
}: {
  serviceId: string;
  domain: string;
  cname: string;
  tags: string[];
  options: WafTag[];
  href: string;
}) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [tagOpen, setTagOpen] = useState(false);
  const mut = useUpdateWafDomainSettings(serviceId, domain, {
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const copy = () => {
    if (!cname) return;
    void navigator.clipboard.writeText(cname);
    show({ tone: 'success', title: 'CNAME copied' });
  };

  return (
    // Stop row-click navigation; the buttons handle their own actions.
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        <Tooltip content="Copy CNAME" side="top">
          <IconButton variant="ghost" size="sm" aria-label="Copy CNAME" disabled={!cname} onClick={copy}>
            <Copy size={14} />
          </IconButton>
        </Tooltip>

        <Popover open={tagOpen} onOpenChange={setTagOpen}>
          <Tooltip content="Edit tags" side="top">
            <PopoverTrigger asChild>
              <IconButton variant="ghost" size="sm" aria-label="Edit tags">
                <Tag size={14} />
              </IconButton>
            </PopoverTrigger>
          </Tooltip>
          <PopoverContent
            align="end"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 'var(--vn-space-3)' }}
          >
            <TagEditor
              value={tags}
              options={options}
              busy={mut.isPending}
              onChange={(next) => mut.mutate({ tags: next })}
            />
          </PopoverContent>
        </Popover>

        <Tooltip content="View detail" side="top">
          <IconButton variant="ghost" size="sm" aria-label="View detail" onClick={() => navigate(href)}>
            <Eye size={14} />
          </IconButton>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export function WafDomainList({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const base = `/services/waf/${serviceId}`;
  const detailHref = (domain: string) => `${base}/domains/view?domain=${encodeURIComponent(domain)}`;
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get('page') || '1')));
  const [pageSize, setPageSize] = useState(() => {
    const p = Number(searchParams.get('per_page') || String(DEFAULT_PAGE_SIZE));
    return PAGE_SIZE_OPTIONS.includes(p) ? p : DEFAULT_PAGE_SIZE;
  });
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const selectedDomains = useMemo(
    () => Object.keys(rowSelection).filter((k) => rowSelection[k]),
    [rowSelection],
  );
  const goBulk = () => {
    if (!selectedDomains.length) return;
    try { sessionStorage.setItem(`waf:${serviceId}:bulk-domains`, JSON.stringify(selectedDomains)); } catch { /* ignore */ }
    navigate(`${base}/domains/bulk`, { state: { domains: selectedDomains } });
  };

  // Filters live in the FilterBuilder popover; seed them from the URL.
  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    const seed: FilterCondition[] = [];
    for (const id of FILTER_FIELD_IDS) {
      const v = searchParams.get(id);
      if (v) seed.push({ id: `init-${id}`, field: id, operator: 'in', value: v });
    }
    return seed;
  });
  const [filterOpen, setFilterOpen] = useState(false);

  // Flag <body> while the filter popover is open so the co-located module CSS can
  // cap the FilterBuilder's portaled menus (see WafDomainList.module.css).
  useEffect(() => {
    if (!filterOpen) return;
    document.body.dataset.wafFilterOpen = 'true';
    return () => {
      delete document.body.dataset.wafFilterOpen;
    };
  }, [filterOpen]);

  // CDN platform + tag catalogs → options for the filter. The page waits for
  // these so the filter UI never renders with a half-populated field.
  const { data: platforms, isLoading: platformsLoading } = useWafPlatforms(serviceId);
  const { data: tags, isLoading: tagsLoading } = useWafTags(serviceId);
  const filtersLoading = platformsLoading || tagsLoading;
  const platformOptions = useMemo(
    () =>
      (platforms ?? []).map((p) => ({ value: p.name, label: p.display_name || p.name })),
    [platforms],
  );
  const tagOptions = useMemo(
    () => (tags ?? []).map((t) => ({ value: t.name, label: t.name })),
    [tags],
  );

  const filterFields: FilterField[] = useMemo(
    () => [
      { value: 'status', label: 'Domain status', input: 'multiselect', operators: [{ value: 'in', label: 'is any of' }], options: STATUS_OPTIONS },
      { value: 'platform_status', label: 'CDN deployment status', input: 'multiselect', operators: [{ value: 'in', label: 'is any of' }], options: PLATFORM_STATUS_OPTIONS },
      { value: 'platform_name', label: 'Installed CDN', input: 'multiselect', operators: [{ value: 'in', label: 'is any of' }], options: platformOptions },
      { value: 'tag', label: 'Tag', input: 'multiselect', operators: [{ value: 'in', label: 'is any of' }], options: tagOptions },
      { value: 'origin_source', label: 'Origin', input: 'text', operators: [{ value: 'in', label: 'is' }] },
      { value: 'cdn_cname', label: 'Custom CDN CNAME', input: 'text', operators: [{ value: 'in', label: 'contains' }] },
    ],
    [platformOptions, tagOptions],
  );

  // Reduce conditions → one comma-joined value per backend field.
  const filterValue = (field: FilterFieldId) =>
    conditions.find((c) => c.field === field && c.value.trim())?.value.trim() || undefined;
  const statusFilter = filterValue('status');
  const platformStatusFilter = filterValue('platform_status');
  const platformNameFilter = filterValue('platform_name');
  const tagFilter = filterValue('tag');
  const originSourceFilter = filterValue('origin_source');
  const cdnCnameFilter = filterValue('cdn_cname');
  const hasFilters = conditions.some((c) => c.value.trim());

  // Keep page/per_page/search + active filters in the URL (matches the other consoles).
  useEffect(() => {
    const params: Record<string, string> = { page: String(page), per_page: String(pageSize) };
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (statusFilter) params.status = statusFilter;
    if (platformStatusFilter) params.platform_status = platformStatusFilter;
    if (platformNameFilter) params.platform_name = platformNameFilter;
    if (tagFilter) params.tag = tagFilter;
    if (originSourceFilter) params.origin_source = originSourceFilter;
    if (cdnCnameFilter) params.cdn_cname = cdnCnameFilter;
    setSearchParams(params, { replace: true });
  }, [
    page, pageSize, debouncedSearch, statusFilter, platformStatusFilter,
    platformNameFilter, tagFilter, originSourceFilter, cdnCnameFilter, setSearchParams,
  ]);

  const { data, isLoading, isError, isFetching, refetch } = useWafDomains(serviceId, {
    page,
    per_page: pageSize,
    search: debouncedSearch.trim() || undefined,
    status: statusFilter,
    platform_status: platformStatusFilter,
    platform_name: platformNameFilter,
    tag: tagFilter,
    origin_source: originSourceFilter,
    cdn_cname: cdnCnameFilter,
  });

  const rows = data?.items ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, data?.pagination?.total_pages ?? 1);

  const columns = useMemo<ColumnDef<WafDomain, unknown>[]>(() => {
    const all: Record<string, ColumnDef<WafDomain, unknown>> = {
      cname: {
        id: 'cname',
        header: 'CNAME',
        enableSorting: false,
        cell: ({ row }) => (row.original.cname ? muted(row.original.cname) : muted('—')),
      },
      status: {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.status)}>
            {row.original.status ? cap(row.original.status) : 'Unknown'}
          </StatusPill>
        ),
      },
      waf: {
        id: 'waf',
        header: 'WAF',
        enableSorting: false,
        cell: ({ row }) => boolBadge(row.original.waf_enabled, 'On'),
      },
      ssl: {
        id: 'ssl',
        header: 'SSL',
        enableSorting: false,
        cell: ({ row }) =>
          boolBadge(
            row.original.ssl_enabled,
            row.original.ssl_type ? sslTypeLabel(row.original.ssl_type) : 'On',
          ),
      },
      platforms: {
        id: 'platforms',
        header: 'Platforms',
        enableSorting: false,
        cell: ({ row }) => {
          // Dedupe by display label — a domain can carry the same platform under
          // multiple bindings, which would otherwise render identical badges.
          const labels = Array.from(
            new Set((row.original.platforms ?? []).map((p) => p.display_name || p.name).filter(Boolean)),
          );
          return labels.length === 0 ? (
            muted('—')
          ) : (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
              {labels.slice(0, 3).map((label) => (
                <Badge key={label} tone="neutral" appearance="subtle" size="sm">
                  {label}
                </Badge>
              ))}
              {labels.length > 3 ? muted(`+${labels.length - 3}`) : null}
            </span>
          );
        },
      },
      tags: {
        id: 'tags',
        header: 'Tags',
        enableSorting: false,
        cell: ({ row }) => <TagsCell tags={row.original.tags ?? []} />,
      },
      created: {
        id: 'created',
        header: 'Created',
        enableSorting: false,
        cell: ({ row }) => muted(fmtEpoch(row.original.created_at)),
      },
    };
    const domainCol: ColumnDef<WafDomain, unknown> = {
      id: 'domain',
      accessorKey: 'domain',
      header: 'Domain',
      cell: ({ row }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
          <Globe size={14} color="var(--vn-fg-muted)" />
          {row.original.domain}
          <a
            href={`https://${row.original.domain}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ display: 'inline-flex', color: 'var(--vn-fg-subtle)' }}
            aria-label="Open domain"
          >
            <ExternalLink size={13} />
          </a>
        </span>
      ),
    };
    const actionsCol: ColumnDef<WafDomain, unknown> = {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <ActionsCell
          serviceId={serviceId}
          domain={row.original.domain}
          cname={row.original.cname}
          tags={row.original.tags ?? []}
          options={tags ?? []}
          href={detailHref(row.original.domain)}
        />
      ),
    };
    return [
      domainCol,
      ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]),
      actionsCol,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, serviceId, tags]);

  return (
    <Container size="xl">
      <PageHeader
        icon={<Globe size={22} />}
        title={`Domains${total ? ` (${total})` : ''}`}
        description="CDN domains for this WAF service. Click a row to view its configuration."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={() => navigate(`/services/waf/${serviceId}/sites/new`)}>
            Add site
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Find domains by name"
            startSlot={<Search />}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />} disabled={filtersLoading}>
                  Filters{hasFilters ? ` (${conditions.filter((c) => c.value.trim()).length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    setConditions(c.filter((x) => x.value.trim()));
                    setPage(1);
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {hasFilters && (
              <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>
                Clear
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              startIcon={<RotateCcw size={15} />}
              onClick={() => refetch()}
              aria-label="Refresh domains list"
            >
              Refresh
            </Button>
          </>
        }
      />

      {isLoading || filtersLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : isError ? (
        <Alert
          tone="danger"
          title="Couldn't load domains"
          actions={<button onClick={() => refetch()}>Retry</button>}
        >
          The WAF service couldn't return its domains. Try again.
        </Alert>
      ) : total === 0 && !debouncedSearch && !hasFilters ? (
        <EmptyState
          icon={<Globe size={20} />}
          title="No domains yet"
          description="This service has no CDN domains configured."
        />
      ) : (
        <>
          {selectedDomains.length > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--vn-space-3)',
                padding: 'var(--vn-space-2) var(--vn-space-3)',
                marginBottom: 'var(--vn-space-3)',
                background: 'var(--vn-bg-subtle, #eef2f0)',
                borderRadius: 'var(--vn-radius-md, 8px)',
              }}
            >
              <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                <strong>{selectedDomains.length}</strong> selected
              </span>
              <div style={{ flex: 1 }} />
              <Button variant="ghost" onClick={() => setRowSelection({})}>Clear</Button>
              <Button startIcon={<SlidersHorizontal size={15} />} onClick={goBulk}>
                Bulk Configure
              </Button>
            </div>
          )}
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => r.domain}
            enableSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            onRowClick={(r) => navigate(detailHref(r.domain))}
            emptyState="No domains match your search or filters."
            footer={`Showing ${rows.length ? (page - 1) * pageSize + 1 : 0}–${(page - 1) * pageSize + rows.length} of ${total}`}
          />
          {(pageCount > 1 || PAGE_SIZE_OPTIONS.length > 1) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-4)',
                marginTop: 'var(--vn-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <label
                  htmlFor="waf-domains-page-size"
                  style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}
                >
                  Rows per page
                </label>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger id="waf-domains-page-size" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={isFetching ? 'Loading…' : `Page ${page} of ${pageCount}`}
                />
              )}
            </div>
          )}
        </>
      )}
    </Container>
  );
}

export default WafDomainList;
